import type { FC } from "react";
import { useState } from "react";
import Menu from "@mui/material/Menu";
import { useTranslation } from "@privacy/translations";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { TrashCanIcon, MoreHorizontalIcon, PlusIcon, PencilIcon } from "../../../../shared/icons";
import type { OrganisationHierarchy } from "../../../../api/organisations.types";
import { prefetch } from "../../../../api/prefetch";
import { tenantConnectedEntitiesQueryOptions } from "../../../../api/tenants";
import { EditTenantDialog } from "./EditTenantDialog";
import { ConfirmDeleteTenantDialog } from "./ConfirmDeleteTenantDialog";
import { AddChildDialog } from "./AddChildDialog";

const MENU_ID = "TenantMenuId";
const ANCHOR_ID = "TenantMenuId";

type TenantMenuProps = {
  tenant: OrganisationHierarchy;
  isRoot: boolean;
  hasConnectedEntities: (tenant: OrganisationHierarchy) => boolean;
};

export const TenantMenu: FC<TenantMenuProps> = ({ tenant, isRoot, hasConnectedEntities }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { organizationNumber, organizationName } = tenant;
  const [openDialog, setOpenDialog] = useState<"add-child" | "confirm-remove" | "edit-tenant" | null>(null);

  const handleCloseDialog = () => setOpenDialog(null);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleDeleteClick = () => {
    setOpenDialog("confirm-remove");
  };

  const isOpen = Boolean(anchorEl);
  return (
    <>
      <IconButton
        aria-label={t("ActionMenu.ButtonLabel")}
        onClick={e => setAnchorEl(e.currentTarget)}
        id={ANCHOR_ID}
        aria-controls={isOpen ? MENU_ID : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        color="primary"
      >
        <MoreHorizontalIcon size={16} color={theme => theme.palette.text.primary} />
      </IconButton>
      <Menu
        onClick={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        id={MENU_ID}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={isOpen}
        slotProps={{
          list: { "aria-labelledby": ANCHOR_ID },
        }}
      >
        <MenuItem onClick={() => setOpenDialog("add-child")}>
          <PlusIcon />
          {t("OrganisationStructure.TenantMenu.AddChild")}
        </MenuItem>

        {!isRoot && (
          <MenuItem onClick={() => setOpenDialog("edit-tenant")}>
            <PencilIcon />
            {t("OrganisationStructure.TenantMenu.Edit")}
          </MenuItem>
        )}

        {!isRoot && (
          <MenuItem
            onClick={handleDeleteClick}
            onMouseEnter={() => void prefetch(tenantConnectedEntitiesQueryOptions(tenant.id))}
          >
            <TrashCanIcon color="currentColor" />
            {t("OrganisationStructure.TenantMenu.Delete")}
          </MenuItem>
        )}
      </Menu>
      {openDialog === "add-child" && <AddChildDialog tenant={tenant} onClose={handleCloseDialog} />}
      {openDialog === "confirm-remove" && (
        <ConfirmDeleteTenantDialog
          tenant={tenant}
          hasConnectedEntities={hasConnectedEntities}
          onClose={handleCloseDialog}
        />
      )}
      {openDialog === "edit-tenant" && (
        <EditTenantDialog
          tenant={tenant}
          onClose={handleCloseDialog}
          defaultValues={{ organizationName, organizationNumber }}
        />
      )}
    </>
  );
};
